import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Box, Grid2, Paper } from '@mui/material';
import CustomAccordion from '../../../../components/ui/materialui/customAccordion';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import TableHeader from '../../../../components/ui/materialui/tableHeader';
import { AddTwoTone, SummarizeTwoTone } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import Breadcrumb from '../../../../components/ui/materialui/breadcrumb';
import { useDataFetch } from '../../../../hooks/useDataFetch2';
import Tabla from '../../../../components/ui/materialui/tablaServerSelector';
import { Dominios, StatusCode, TipoArchivo } from '../../../../utils/enums';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useToast } from '../../../../hooks/useToast';
import { ValidadorMinvuDto } from '../../validarBeneficios/models/validadorMinvu';
import Filtros, { IFilter } from '../components/filtros';
import ModalListSeguros from '../components/modalListSeguros';
import { useColumns } from '../utils/columns';
import useArchivoMinvu from '../hooks/useArchivoMinvu';
import { ArchivoMinvuDTO } from '../models/cobroSeguros';
import { format } from 'rut.js';

const validationSchema = Yup.object<IFilter>().shape({
    numeroOperacion: Yup.string(),
    rut: Yup.string(),
    nombre: Yup.string(),
    tipoBeneficio: Yup.string().required("Campo requerido"),
    estado: Yup.string().nullable(),
    fechaDesde: Yup.string().nullable(),
    fechaHasta: Yup.string().nullable(),
});

const CobroSeguro: React.FC = () => {

    const [tablaSeleccionada, setTablaSeleccionada] = useState<ArchivoMinvuDTO[]>([]);
    const [show, setShow] = useState<boolean>(false);
    const urlTabla = `siscred-api-dividendos/v1/minvu/listar-archivo-minvu`;
    const { descargarArchivo } = useArchivoMinvu();
    // // Calcular fechas por defecto
    const defaultFechaDesde = new Date(new Date().setMonth(new Date().getMonth() - 1));
    const defaultFechaHasta = new Date();

    // Crear los parámetros de búsqueda con fechas
    const searchParams = new URLSearchParams({
        'filters.tipoBeneficio': 'DS_01',
        'filters.fechaDesde': defaultFechaDesde.toISOString(),
        'filters.fechaHasta': defaultFechaHasta.toISOString(),
    });

    const controlTabla = useDataFetch(
        urlTabla,
        searchParams,
        true,
    );

    const defaultValues = {
        numeroOperacion: '',
        rut: '',
        nombre: '',
        tipoBeneficio: "DS_01",
        estado: '',
        fechaDesde: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0], // Default to 1 month ago
        fechaHasta: new Date().toISOString().split('T')[0] // Default to today,
    }

    const {
        formState: { errors },
        control,
        handleSubmit,
        watch,
        reset
    } = useForm<IFilter>({
        resolver: yupResolver(validationSchema),
        defaultValues: defaultValues,
        mode: 'onBlur'
    })

    const limpiarFiltros = () => {
        controlTabla.manejarCambiosFiltros('filters.numeroOperacion', '');
        controlTabla.manejarCambiosFiltros('filters.rut', '');
        controlTabla.manejarCambiosFiltros('filters.nombre', '');
        controlTabla.manejarCambiosFiltros('filters.tipoBeneficio', '')
        controlTabla.manejarCambiosFiltros('filters.estado', '');
        controlTabla.manejarCambiosFiltros('filters.estado', '');
        controlTabla.manejarCambiosFiltros('filters.fechaDesde', '')
        controlTabla.manejarCambiosFiltros('filters.fechaHasta', '')
    }

    const onSubmit = (data: IFilter) => {
        limpiarFiltros();

        if (data.numeroOperacion) {
            controlTabla.manejarCambiosFiltros('filters.numeroOperacion', data.numeroOperacion);
        }
        if (data.rut) {
            controlTabla.manejarCambiosFiltros('filters.rut', format(data.rut, { dots: false }));
        }
        if (data.nombre) {
            controlTabla.manejarCambiosFiltros('filters.nombre', data.nombre);
        }

        if (data.tipoBeneficio) {
            controlTabla.manejarCambiosFiltros(
                'filters.tipoBeneficio',
                data.tipoBeneficio,
            )
        }

        if (data.estado) {
            controlTabla.manejarCambiosFiltros('filters.estado', data.estado);
        }

        if (data.fechaDesde) {
            controlTabla.manejarCambiosFiltros(
                'filters.fechaDesde',
                data.fechaDesde,
            )
        }
        if (data.fechaHasta) {
            controlTabla.manejarCambiosFiltros(
                'filters.fechaHasta',
                data.fechaHasta,
            )
        }

        controlTabla.fetchData();
    }

    useEffect(() => {
        reset(defaultValues)
        //eslint-disable-next-line
    }, []);

    const { COLUMNS } = useColumns();
    //eslint-disable-next-line
    const columns = useMemo(() => COLUMNS, [controlTabla]);

    const handleGenerarArchivo = async () => {
        await descargarArchivo(tablaSeleccionada, controlTabla.fetchData);
    }

    const breadcrumbItems = [
        { label: 'Inicio', url: '/' },
        { label: 'MINVU', url: `/minvu` },
        { label: 'Cobro seguro cesantía', active: true },
    ]

    return (
        <>
            <Suspense>
                <Breadcrumb items={breadcrumbItems} />
                <Box sx={{ mt: 2 }}>
                    <Paper elevation={3} sx={{ p: 3 }}>
                        <Suspense>
                            <CustomAccordion
                                title='Filtros'
                                expandedDefault
                            >
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <Filtros
                                        control={control}
                                        errors={errors}
                                        watch={watch}
                                    />

                                </form>
                            </CustomAccordion>
                            <Tabla
                                columns={columns}
                                control={controlTabla}
                                url={urlTabla}
                                esMultiple={true}
                                setFilaSeleccionada={setTablaSeleccionada}
                                titulo={
                                    <TableHeader
                                        title=""
                                        control={controlTabla}
                                        button={
                                            <MButton
                                                type="button"
                                                variant="contained"
                                                disabled={tablaSeleccionada.length === 0}
                                                onClick={() => { handleGenerarArchivo() }}
                                                startIcon={<AddTwoTone />}
                                            >
                                                Generar archivo MINVU
                                            </MButton>
                                        }
                                        buttonExport={
                                            <MButton
                                                type="button"
                                                variant="contained"
                                                // disabled={tablaSeleccionada.length === 0}
                                                onClick={() => setShow(true)}
                                                startIcon={<SummarizeTwoTone />}
                                            >
                                                Ver listados MINVU
                                            </MButton>
                                        }
                                    />
                                }
                            />
                        </Suspense>
                    </Paper>
                </Box>
            </Suspense>
            <ModalListSeguros
                show={show}
                onHide={() => setShow(false)}
                tipoArchivo={TipoArchivo.TIPOARCHIVOMINVU_VALIDACION}
            />
        </>
    );
};

export default CobroSeguro;